/**
 * client/src/cma/compFigures.ts — a comparable sale's figures, as `Field`s.
 *
 * WHY THIS FILE EXISTS AT ALL. The comparable card shows three large numbers, and one of them —
 * price per square foot — is not on any record anywhere. It is a division this app performs. The
 * whole argument of this product is that a reader can click any number and be told, in one step,
 * where it came from; a computed figure that looked exactly like a sourced one would be the single
 * most damaging thing a "beautiful" card could do.
 *
 * So every figure a card shows is built here, as a `Field` from the Recommendation contract, and
 * rendered through `ProvenanceChip` — the same component the result screen uses. That gives each
 * one the two-tag treatment the project's provenance model requires:
 *
 *     $612,000  〔Collin CAD 2026〕                  a value read straight from a source: ONE tag
 *     $257 /SF  〔Collin CAD 2026〕〔computed〕        a value this app derived: SOURCE + STATE
 *     $257 /SF  〔You entered〕〔computed〕            derived from a figure the agent supplied
 *     —         〔Not stated〕                        an absence, with the reason
 *
 * NOTHING HERE ROUNDS A SOURCE FIGURE. `money()` and `sqft()` group digits for reading and change
 * no value. `pricePerSf` is the only place a new number is produced, it is produced from two
 * numbers that are themselves on the card, and its `formula` states the division in the figures the
 * reader can see.
 *
 * UNKNOWN IS NEVER ZERO. A missing price does not make $/SF zero, or "—/SF", or 0. It makes the
 * whole figure a gap with a reason, and `tier: "gap"` renders it as an absence rather than as a
 * small number.
 */
import type { Field, Tier } from "../types/recommendation";
import type { CandidateComp, SubjectGrid } from "./types";
import { fieldSourceLabel } from "./cascade";

/* --------------------------------------------------------------------------- *
 * Display — grouping digits, never changing a value                            *
 * --------------------------------------------------------------------------- */

export function money(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return `$${Math.round(n).toLocaleString("en-US")}`;
}

export function sqft(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return `${Math.round(n).toLocaleString("en-US")} SF`;
}

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

/* --------------------------------------------------------------------------- *
 * Provenance — asked of the row, never assumed                                 *
 * --------------------------------------------------------------------------- */

/** Did a human supply or correct this field? `_raw` holds the as-pulled value from the first edit. */
export function isHumanSupplied(comp: CandidateComp, field: string): boolean {
  return comp._raw != null && field in comp._raw;
}

/**
 * The name of the place this field came from — the field's own label where the row carries one,
 * otherwise the row's. `"You entered"` once a human has changed it, because after an edit the
 * source did not say this number and must not be quoted as though it had.
 */
export function sourceOf(comp: CandidateComp, field: string): string {
  if (isHumanSupplied(comp, field)) return "You entered";
  return fieldSourceLabel(comp, field) ?? comp._source ?? "Source not stated on this record";
}

/**
 * The tier for a figure that is on the record.
 *
 * A GAP IS AN ABSENT VALUE, NOT AN ABSENT SOURCE NAME. Those are different facts and conflating
 * them is the more dangerous mistake in both directions: showing "—" for a figure the record
 * actually carries HIDES a number the agent needs, and calling an unattributed figure "Sourced"
 * claims an attribution nobody made.
 *
 *   absent value                     -> gap        "—", with the reason
 *   human supplied or corrected      -> entered    "You entered"
 *   present, and the row names where -> verified   "Sourced"
 *   present, and it does not         -> indicative the figure stands, its origin is not stated
 *
 * FLAG (design-tokens.ts already carries the same one): the tier vocabulary has five values and the
 * ratified chip table has four colours, and none of the five was written for "the value is real and
 * its source is unnamed". `indicative` is this file's best-fit reading and is deliberately NOT
 * `verified`. The UI/Design-System owner should confirm or correct it.
 */
function tierFor(comp: CandidateComp, field: string, value: unknown): Tier {
  if (value == null || value === "") return "gap";
  if (isHumanSupplied(comp, field)) return "entered";
  return fieldSourceLabel(comp, field) || comp._source ? "verified" : "indicative";
}

/** Why a figure is absent. Structural where we can say so, and never blamed on a file that
 *  does not exist — the address-only path has no upload to be short of anything. */
function gapReason(comp: CandidateComp, label: string): string {
  return comp._source
    ? `${comp._source} carries no ${label.toLowerCase()} for this sale.`
    : `No source on record states a ${label.toLowerCase()} for this sale.`;
}

function fieldId(index: number, key: string): string {
  return `comp-${index}-${key}`;
}

/** One figure, straight from the record. One tag: where it came from. */
export function plainFigure(
  comp: CandidateComp,
  index: number,
  key: string,
  label: string,
  value: number | string | null | undefined,
  display: string,
): Field {
  const tier = tierFor(comp, key, value);
  return {
    id: fieldId(index, key),
    label,
    // The figure, whenever there IS one. Only a genuine absence renders as a dash.
    display: tier === "gap" ? "—" : display,
    tier,
    source: tier === "gap" ? "Not found" : sourceOf(comp, key),
    ...(tier === "gap" ? { reason: gapReason(comp, label) } : null),
    ...(tier === "indicative"
      ? { reason: `This record carries the figure but does not say where it came from.` }
      : null),
  };
}

/**
 * PRICE PER SQUARE FOOT — the one number on the card that no record states.
 *
 * Two tags, always: the source of the figures it was derived FROM, and `computed` as the state.
 * When either input was supplied by a human the whole result is `You entered`, because a division
 * of a number the agent typed is a number the agent is responsible for — the source cannot be
 * quoted for it even though the source supplied the other half.
 *
 * `inputs` point at the two `Field` ids on the same card, which is what makes the drawer's trace
 * recursive: a reader who does not accept $257 can open it, see the division, and open either
 * figure it divided.
 */
export function pricePerSf(comp: CandidateComp, index: number): Field {
  const price = comp.price;
  const gla = comp.gla;
  const id = fieldId(index, "ppsf");
  const usable = price != null && Number.isFinite(price) && price > 0
    && gla != null && Number.isFinite(gla) && gla > 0;

  if (!usable) {
    const missing = [
      price == null || !(price > 0) ? "a sold price" : null,
      gla == null || !(gla > 0) ? "a living area" : null,
    ].filter(Boolean).join(" or ");
    return {
      id,
      label: "Price per square foot",
      display: "—",
      tier: "gap",
      source: "Not stated",
      // The honest statement: not "we do not know the $/SF", but "we cannot divide without both".
      reason: `This sale has no ${missing} on record, so a price per square foot cannot be worked out. It is blank, not zero.`,
    };
  }

  const human = isHumanSupplied(comp, "price") || isHumanSupplied(comp, "gla");
  const named = !!(fieldSourceLabel(comp, "price") || comp._source);
  return {
    id,
    label: "Price per square foot",
    display: `$${Math.round((price as number) / (gla as number)).toLocaleString("en-US")}`,
    // The derived figure inherits the weaker of its inputs' standings, never the stronger.
    tier: human ? "entered" : named ? "verified" : "indicative",
    source: human ? "You entered" : sourceOf(comp, "price"),
    // THE STATE TAG. Present only because this value was transformed; a figure read straight from a
    // record carries the source tag alone.
    state: "computed",
    how: "The sold price divided by the living area. Nothing else is applied — no adjustment, no rounding of either input.",
    formula: `${money(price)} ÷ ${sqft(gla)}`,
    inputs: [
      { label: "Sold price", ref: fieldId(index, "price") },
      { label: "Living area", ref: fieldId(index, "gla") },
    ],
    rows: [
      { label: "Sold price", value: money(price), ref: fieldId(index, "price") },
      { label: "Living area", value: sqft(gla), ref: fieldId(index, "gla") },
    ],
  };
}

/** The three figures a card leads with, in the order an agent reads them. */
export function headlineFigures(comp: CandidateComp, index: number): Field[] {
  return [
    plainFigure(comp, index, "price", "Sold price", comp.price, money(comp.price)),
    plainFigure(comp, index, "gla", "Living area", comp.gla, sqft(comp.gla)),
    pricePerSf(comp, index),
  ];
}

/* --------------------------------------------------------------------------- *
 * How this sale differs from the subject                                       *
 * --------------------------------------------------------------------------- */

export interface Delta {
  /** "38 SF larger", "1 year older". Already a sentence fragment, already directional. */
  text: string;
  /** Which way it cuts, for the eye only — the words above say it too. */
  sense: "up" | "down" | "level";
}

function subjectNumber(grid: SubjectGrid | null | undefined, key: string): number | null {
  const state = grid?.[key];
  if (!state || !state.checked) return null;
  const v = state.value;
  const n = typeof v === "number" ? v : typeof v === "string" ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
}

/**
 * The differences between this sale and the subject, in words.
 *
 * COMPUTED, AND SAID SO BY THE CARD THAT RENDERS THEM. These are subtractions of two figures that
 * are both on screen, and they are the reason an agent picks one sale over another — but they are
 * emphatically NOT adjustments. Nothing here is a dollar amount and nothing here goes near the
 * engine; `engine.amb` does the adjusting, from the same source figures, on its own terms.
 *
 * A difference is omitted rather than guessed when either side is missing. An unknown living area
 * does not make a sale "the same size".
 */
export function deltasVsSubject(comp: CandidateComp, grid: SubjectGrid | null | undefined): Delta[] {
  const out: Delta[] = [];

  const sGla = subjectNumber(grid, "gla");
  if (sGla != null && comp.gla != null && Number.isFinite(comp.gla)) {
    const d = Math.round(comp.gla - sGla);
    out.push(d === 0
      ? { text: "same living area", sense: "level" }
      : { text: `${Math.abs(d).toLocaleString("en-US")} SF ${d > 0 ? "larger" : "smaller"}`, sense: d > 0 ? "up" : "down" });
  }

  const sYear = subjectNumber(grid, "year");
  if (sYear != null && comp.year != null && Number.isFinite(comp.year)) {
    const d = Math.round(comp.year - sYear);
    out.push(d === 0
      ? { text: "same year built", sense: "level" }
      : { text: `${Math.abs(d)} year${Math.abs(d) === 1 ? "" : "s"} ${d > 0 ? "newer" : "older"}`, sense: d > 0 ? "up" : "down" });
  }

  const sLot = subjectNumber(grid, "lot_sf");
  if (sLot != null && comp.lot_sf != null && Number.isFinite(comp.lot_sf)) {
    const d = Math.round(comp.lot_sf - sLot);
    if (d !== 0) {
      out.push({
        text: `${Math.abs(d).toLocaleString("en-US")} SF ${d > 0 ? "bigger" : "smaller"} lot`,
        sense: d > 0 ? "up" : "down",
      });
    }
  }

  return out;
}

/** "4 bed · 2.1 bath" — and nothing at all when neither is on record. */
export function bedBathLine(comp: CandidateComp): string | null {
  const beds = comp.beds != null && Number.isFinite(comp.beds) ? `${comp.beds} bed` : null;
  const full = comp.full_baths;
  const half = comp.half_baths;
  const baths = full != null && Number.isFinite(full)
    ? `${full}${half != null && Number.isFinite(half) && half > 0 ? `.${half}` : ""} bath`
    : null;
  const parts = [beds, baths].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}
